import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { PostMedia } from '@gik/stream/types/Stream';
import { Badge } from '@gik/ui/Badge';
import { Grid } from '@gik/ui/Grid';
import { KeepProportions, Square, Wide } from '@gik/ui/KeepProportions';
import { KeepProportionRange } from '@gik/ui/KeepProportions/KeepProportionRange';
import { UI } from '@gik/ui/UIManager';
import { LightboxMedia } from '@gik/ui/UIManager/UILightboxTypes';
import React from 'react';

export interface IAttachmentPreviewProps extends UIComponent {
  media: PostMedia[];
}

const blockName = 'attachment-preview';
const previewLimit = 4;

function AttachmentPreviewComp({ media, className, ...otherProps }: IAttachmentPreviewProps) {
  const bem = useBemCN(blockName);
  const preview = React.useMemo(() => media.slice(0, previewLimit), [media]);
  const moreIndicator = React.useMemo(() => media.slice(previewLimit).length, [media]);

  const onClickHandler = React.useCallback(
    (i: number) => {
      UI.lightboxFull(
        media.map(m => ({
          mediaType: LightboxMedia.IMAGE,
          url: m.url,
        })),
        {
          mode: 'bright',
        },
        i
      );
    },
    [media]
  );

  return (
    <div {...bem(null, null, className)} {...otherProps}>
      {preview.length > 1 && (
        <Grid {...bem('grid')} cols={preview.length >= 4 ? 2 : preview.length} fixed>
          {preview.map((m, i) => (
            <AttachmentImagePreview
              {...bem('preview')}
              key={m.id}
              src={m.thumbnailUrl}
              onClick={() => void onClickHandler(i)}
            />
          ))}
        </Grid>
      )}
      {preview.length === 1 && (
        <KeepProportionRange
          {...bem('image')}
          proportionUpper={Square}
          proportionLower={Wide}
          src={preview[0].url}
          onImageClick={() => void onClickHandler(0)}
        />
      )}
      {moreIndicator > 0 && (
        <Badge {...bem('more-indicator')} pill variant={'primary'} onClick={() => void onClickHandler(previewLimit)}>
          AND {moreIndicator} MORE
        </Badge>
      )}
    </div>
  );
}

export const AttachmentPreview = withComponentErrorBoundary(AttachmentPreviewComp);

function AttachmentImagePreview({ src, onClick, className }: { src: string; onClick: () => void; className?: string }) {
  const bem = useBemCN('attachment-image-preview');

  return (
    <div {...bem(null, null, className)}>
      <KeepProportions {...bem('image')} proportion={Square} src={src} onImageClick={onClick} />
    </div>
  );
}
